import React, { useState, useCallback } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

import labels from "../../../constants/displayFields";
import GroupedAssetTableSkeleton from "./GroupedAssetTableSkeleton";
import AssetDetailsModal from "../../../views/inventory/requests/asset-details/AssetDetailsModal";
import useMutation from "../../../hooks/useMutation";
import { CREATE_KIT_ASSET_MUTATION } from "../../../constants/graphql/mutations";
import useQuery from "../../../hooks/useQuery";
import useNotification from "../../../hooks/notification";

const styles = {
  tableContainer: {
    maxHeight: "calc(70vh - 120px)",
    overflow: "auto",
  },
};

export default function GroupedAssetTable({
  collection,
  projectId,
  initialLoading,
  loading,
  hasNextPage,
  onScroll,
}) {
  const { notifySuccess } = useNotification();
  const [createKitAsset, { loading: createKitAssetLoading }] = useMutation(
    CREATE_KIT_ASSET_MUTATION
  );
  const query = useQuery();
  const [quantities, setQuantities] = useState({});

  const handleQuantityChange = useCallback((skuNumber, value) => {
    const quantity = value === "" ? "" : parseInt(value, 10);
    if (quantity >= 0 || quantity === "") {
      setQuantities(prev => ({
        ...prev,
        [skuNumber]: quantity,
      }));
    }
  }, []);

  const handleAddToKit = useCallback(
    async asset => {
      const quantity = quantities[asset.skuNumber];
      if (!quantity || quantity <= 0) return;

      const kitId = query.get("kitId");

      try {
        createKitAsset({
          variables: {
            kitId,
            input: { quantity, assetId: asset.associatedAssetIds[0] },
          },
        });
        notifySuccess("Components successfully added to kit");
        setQuantities(prev => ({
          ...prev,
          [asset.skuNumber]: "",
        }));
      } catch (error) {
        console.error("Error adding assets to kit:", error);
      }
    },
    [quantities, query, createKitAsset, notifySuccess]
  );

  return (
    <TableContainer component={Paper} elevation={0} sx={styles.tableContainer} onScroll={onScroll}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center">{labels.sku_number}</TableCell>
            <TableCell align="center">Associated Item IDs</TableCell>
            <TableCell align="center">Description</TableCell>
            <TableCell align="center">Total Available Quantity</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {initialLoading && collection.length === 0 && <GroupedAssetTableSkeleton count={8} />}
          {collection.map((asset, index) => (
            <TableRow key={`${asset.skuNumber}-${index}`} hover>
              <TableCell align="center">{asset.skuNumber}</TableCell>
              <TableCell align="center">
                {asset.associatedItemIds && asset.associatedItemIds.length > 0
                  ? asset.associatedItemIds.map((itemId, idx) => (
                      <div key={idx}>
                        <AssetDetailsModal
                          asset={{
                            id: asset.associatedAssetIds[idx],
                            itemId: itemId,
                            project: { id: projectId },
                          }}
                        />
                      </div>
                    ))
                  : "—"}
              </TableCell>
              <TableCell align="center">{asset.description}</TableCell>
              <TableCell align="center">{asset.availableQuantity}</TableCell>
              <TableCell align="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    type="number"
                    value={quantities[asset.skuNumber] || ""}
                    onChange={e => handleQuantityChange(asset.skuNumber, e.target.value)}
                    placeholder="Qty"
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 80 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                    disabled={
                      !quantities[asset.skuNumber] ||
                      quantities[asset.skuNumber] <= 0 ||
                      createKitAssetLoading
                    }
                    onClick={() => handleAddToKit(asset)}
                  >
                    Add to Kit
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {loading && hasNextPage && <GroupedAssetTableSkeleton count={3} />}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
